// Slice 44 — Telemetry screen. Reads from /admin/telemetry/summary;
// renders KPI strip + feature usage ranked + errors-by-key + version
// distribution. All four panels degrade to a "no telemetry in window"
// message rather than rendering empty bars.
//
// The window length is selectable (7 / 30 / 90 days) but defaults to 30
// — the same default the backend bakes in.

import { useEffect, useState } from "react";
import { TelemetryApi, type TelemetrySummary } from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import { HqBadge, HqButton, HqCard, HqStatusDot, Icons } from "../../ui";

type Window = 7 | 30 | 90;

export function TelemetryScreen() {
  const { refreshTick, toast } = useShell();
  const [windowDays, setWindowDays] = useState<Window>(30);
  const [summary, setSummary] = useState<TelemetrySummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void refreshTick;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const res = await TelemetryApi.summary(windowDays);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        toast(res.error.message, "danger");
        setSummary(null);
        return;
      }
      setSummary(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [windowDays, refreshTick, toast]);

  function exportCsv() {
    if (!summary) return;
    const lines: string[] = [];
    lines.push("# Vyvern HQ — telemetry summary");
    lines.push(`# windowDays=${summary.window.days}`);
    lines.push(`# fromTs=${summary.window.fromTs} toTs=${summary.window.toTs}`);
    lines.push("");
    lines.push("section,key,count");
    lines.push(`kpi,reportingInstances,${summary.reportingInstances}`);
    lines.push(`kpi,activeInstances,${summary.activeInstances}`);
    lines.push(`kpi,commandInvocations,${summary.commandInvocations}`);
    lines.push(`kpi,errorTotal,${summary.errorTotal}`);
    lines.push(`kpi,errorRate,${summary.errorRate.toFixed(6)}`);
    for (const v of summary.byVersion) lines.push(`version,${v.vyvernVersion},${v.count}`);
    for (const f of summary.feature) lines.push(`feature,${f.key},${f.count}`);
    for (const e of summary.errors) lines.push(`error,${e.key},${e.count}`);
    const blob = new Blob([`${lines.join("\n")}\n`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `telemetry-${summary.window.days}d-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Header
        windowDays={windowDays}
        onWindow={setWindowDays}
        onExport={exportCsv}
        disabled={!summary}
      />
      {summary ? (
        <>
          <KpiStrip summary={summary} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <FeatureUsage feature={summary.feature} />
            <Errors errors={summary.errors} />
          </div>
          <Versions byVersion={summary.byVersion} />
        </>
      ) : (
        <HqCard>
          <p
            style={{
              margin: 0,
              color: "var(--hq-text-dim)",
              fontSize: 13,
              textAlign: "center",
              padding: "16px 0",
            }}
          >
            {loading ? "Loading…" : "Failed to load telemetry."}
          </p>
        </HqCard>
      )}
    </div>
  );
}

function Header({
  windowDays,
  onWindow,
  onExport,
  disabled,
}: {
  windowDays: Window;
  onWindow: (w: Window) => void;
  onExport: () => void;
  disabled: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <Icons.Telemetry width={20} height={20} sw={1.7} style={{ color: "var(--hq-accent)" }} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-.02em",
            color: "var(--hq-text)",
            lineHeight: 1.2,
          }}
        >
          Telemetry
        </h1>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          Aggregated · opt-in · no PII · last {windowDays} days
        </span>
      </div>
      <span style={{ flex: 1 }} />
      <Segmented
        options={[
          { value: "7", label: "7d" },
          { value: "30", label: "30d" },
          { value: "90", label: "90d" },
        ]}
        value={String(windowDays)}
        onChange={(v) => onWindow(Number(v) as Window)}
      />
      <HqButton
        variant="secondary"
        leadingIcon={Icons.Download}
        onClick={onExport}
        disabled={disabled}
      >
        Export CSV
      </HqButton>
    </div>
  );
}

function KpiStrip({ summary }: { summary: TelemetrySummary }) {
  const errorPct = (summary.errorRate * 100).toFixed(2);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
      <Kpi label="Reporting instances" value={summary.reportingInstances.toString()} />
      <Kpi
        label="Active instances"
        value={summary.activeInstances.toString()}
        sub="(latest per license)"
      />
      <Kpi label="Command invocations" value={summary.commandInvocations.toLocaleString()} />
      <Kpi
        label="Error rate"
        value={`${errorPct}%`}
        sub={`${summary.errorTotal.toLocaleString()} errors`}
        tone={summary.errorRate > 0.05 ? "danger" : summary.errorRate > 0.01 ? "warn" : "success"}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "success" | "warn" | "danger";
}) {
  const color =
    tone === "danger"
      ? "var(--hq-danger)"
      : tone === "warn"
        ? "var(--hq-warn)"
        : tone === "success"
          ? "var(--hq-success)"
          : "var(--hq-text)";
  return (
    <div
      style={{
        padding: 16,
        background: "var(--hq-bg-raised)",
        borderRadius: "var(--hq-radius-2xl)",
        border: "1px solid var(--hq-border)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 10,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{ fontSize: 26, fontWeight: 600, color, lineHeight: 1, letterSpacing: "-.02em" }}
      >
        {value}
      </span>
      {sub && <span style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>{sub}</span>}
    </div>
  );
}

function FeatureUsage({ feature }: { feature: TelemetrySummary["feature"] }) {
  const max = feature.reduce((m, f) => Math.max(m, f.count), 0) || 1;
  return (
    <HqCard title="Feature usage · ranked" icon={Icons.Activity}>
      {feature.length === 0 ? (
        <Empty />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {feature.map((f) => (
            <FeatureBar key={f.key} label={f.key} count={f.count} max={max} />
          ))}
        </div>
      )}
    </HqCard>
  );
}

function FeatureBar({ label, count, max }: { label: string; count: number; max: number }) {
  const pct = Math.max(2, (count / max) * 100);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          className="hq-mono"
          style={{
            fontSize: 11,
            color: "var(--hq-text-muted)",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text)" }}>
          {count.toLocaleString()}
        </span>
      </div>
      <div
        style={{
          height: 6,
          width: "100%",
          background: "var(--hq-bg-input)",
          borderRadius: 99,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: "linear-gradient(90deg, var(--hq-accent-deep), var(--hq-accent))",
            boxShadow: "0 0 4px rgba(34,211,238,.55)",
          }}
        />
      </div>
    </div>
  );
}

function Errors({ errors }: { errors: TelemetrySummary["errors"] }) {
  return (
    <HqCard title="Errors · last window" icon={Icons.Audit}>
      {errors.length === 0 ? (
        <Empty message="No errors reported." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {errors.map((e) => (
            <div
              key={e.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                background: "var(--hq-bg-input)",
                border: "1px solid var(--hq-border-input)",
                borderRadius: "var(--hq-radius-md)",
              }}
            >
              <HqStatusDot
                tone={e.count > 10 ? "danger" : e.count > 5 ? "warn" : "neutral"}
                size={8}
              />
              <span
                className="hq-mono"
                style={{
                  fontSize: 11,
                  color: "var(--hq-text)",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {e.key}
              </span>
              <HqBadge tone={e.count > 10 ? "danger" : e.count > 5 ? "warn" : "neutral"}>
                {e.count.toLocaleString()}
              </HqBadge>
            </div>
          ))}
        </div>
      )}
    </HqCard>
  );
}

function Versions({ byVersion }: { byVersion: TelemetrySummary["byVersion"] }) {
  const total = byVersion.reduce((s, v) => s + v.count, 0);
  return (
    <HqCard title="Version distribution" icon={Icons.Sparkles}>
      {byVersion.length === 0 ? (
        <Empty />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {byVersion.map((v, i) => {
            const pct = total > 0 ? (v.count / total) * 100 : 0;
            const color = TONE_RING[i % TONE_RING.length] ?? "var(--hq-accent)";
            return (
              <div
                key={v.vyvernVersion}
                style={{ display: "flex", flexDirection: "column", gap: 4 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text)" }}>
                    v{v.vyvernVersion}
                  </span>
                  <span style={{ flex: 1 }} />
                  <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
                    {v.count.toLocaleString()} · {pct.toFixed(1)}%
                  </span>
                </div>
                <div
                  style={{
                    height: 8,
                    width: "100%",
                    background: "var(--hq-bg-input)",
                    borderRadius: 99,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(2, pct)}%`,
                      height: "100%",
                      background: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </HqCard>
  );
}

const TONE_RING = [
  "var(--hq-accent)",
  "var(--hq-tier-pro)",
  "var(--hq-info)",
  "var(--hq-success)",
  "var(--hq-warn)",
];

function Empty({ message = "No telemetry in this window." }: { message?: string }) {
  return (
    <div
      style={{
        padding: 24,
        textAlign: "center",
        color: "var(--hq-text-dim)",
        fontSize: 12.5,
      }}
    >
      {message}
    </div>
  );
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 3,
        gap: 2,
        background: "var(--hq-bg-input)",
        borderRadius: "var(--hq-radius-lg)",
        border: "1px solid var(--hq-border-input)",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              padding: "0 10px",
              height: 26,
              borderRadius: "var(--hq-radius-md)",
              background: active ? "var(--hq-bg-active)" : "transparent",
              color: active ? "var(--hq-text)" : "var(--hq-text-muted)",
              fontSize: 11.5,
              border: "none",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
